const API_ORIGIN =
  process.env.REACT_APP_API_ORIGIN ||
  process.env.REACT_APP_TASK_API_ORIGIN ||
  "https://fundswalla.in";

const buildAbsoluteUrl = (prefix, endpoint) =>
  `${API_ORIGIN}${prefix}/${String(endpoint).replace(/^\//, "")}`;

const buildRelativeUrl = (prefix, endpoint) =>
  `${prefix}/${String(endpoint).replace(/^\//, "")}`;

/**
 * Local (npm start): /Api/... → setupProxy → fundswalla.in (no CORS)
 * Live/production: direct https://fundswalla.in/Api/...
 *
 * TaskApi = apply-now + shared (getState, saveLendingPage, OTP on apply-now, etc.)
 * AutomationApi = apply-new only — do NOT point taskApiPostUrl at AutomationApi
 * or apply-now will break.
 */
export const taskApiPostUrl = (endpoint) =>
  process.env.NODE_ENV === "development"
    ? buildRelativeUrl("/Api/TaskApi", endpoint)
    : buildAbsoluteUrl("/Api/TaskApi", endpoint);

/** apply-new AutomationApi only */
export const automationApiPostUrl = (endpoint) =>
  process.env.NODE_ENV === "development"
    ? buildRelativeUrl("/Api/AutomationApi", endpoint)
    : buildAbsoluteUrl("/Api/AutomationApi", endpoint);

/** GET endpoints (e.g. getState) — same origin rules as POST. */
export const taskApiGetUrl = (endpoint) => taskApiPostUrl(endpoint);

export const automationApiGetUrl = (endpoint) => automationApiPostUrl(endpoint);

/** Pincode rows may use `pincode` (current API) or legacy `m_pincode_value`. */
export const getPincodeValueFromRow = (row) =>
  String(row?.m_pincode_value ?? row?.pincode ?? "").trim();

export const taskApiDirectUrl = (endpoint) =>
  buildAbsoluteUrl("/Api/TaskApi", endpoint);

export const automationApiDirectUrl = (endpoint) =>
  buildAbsoluteUrl("/Api/AutomationApi", endpoint);

/**
 * GET (profile): always same-origin /loan_backend/...
 * Local: setupProxy. Live: nginx GET already works.
 *
 * POST (login, lead save): local relative, live absolute.
 * Live nginx currently returns 405 for POST /loan_backend/.
 */
export const loanBackendUrl = (endpoint) =>
  buildRelativeUrl("/loan_backend", endpoint);

export const loanBackendPostUrl = (endpoint) =>
  process.env.NODE_ENV === "development"
    ? buildRelativeUrl("/loan_backend", endpoint)
    : buildAbsoluteUrl("/loan_backend", endpoint);

/** GET loan_backend — local relative via proxy, live absolute */
export const loanBackendGetUrl = (endpoint) =>
  process.env.NODE_ENV === "development"
    ? buildRelativeUrl("/loan_backend", endpoint)
    : buildAbsoluteUrl("/loan_backend", endpoint);

/** Lead save via TaskApi (apply-now / landing / apply-loan) */
export const saveLendingPageUrl = () => taskApiPostUrl("saveLendingPage");
/** /apply-loan single lead-save endpoint */
export const saveLendingPage1Url = () => taskApiPostUrl("saveLendingPage1");

/** Penny drop — POST /bank_ac_verification (apply-new only) */
export const bankAcVerificationUrl = () =>
  process.env.NODE_ENV === "development"
    ? "/bank_ac_verification"
    : `${API_ORIGIN}/bank_ac_verification`;

/** apply-new only — AutomationApi BRE / credit / AA / sanction */
export const locationBreUrl = () => automationApiPostUrl("locationBre");
export const creditAnalyticsUrl = () =>
  automationApiPostUrl("getCreditAnalyticsCF");
/** apply-new mobile BRE — live path is /Api/AutomationApi/bureauBRE (not /automationApi/) */
export const bureauMobileBreUrl = () =>
  automationApiPostUrl("bureauBRE");
export const generateAccountAggregatorUrl = () =>
  automationApiPostUrl("generateAccountAggregatorUrl");
export const checkAccountAggregatorStatus = () =>
  automationApiPostUrl("checkAccountAggregatorStatus");
export const sanctionLetterDataUrl = () =>
  automationApiPostUrl("sanctionlatterdata");
export const sanctionLetterViewUrl = () =>
  automationApiPostUrl("sanctionlatterview");

/** apply-new AutomationApi helpers (OTP / PAN / email / Aadhaar send) */
export const automationSendOtpUrl = () => automationApiPostUrl("sendOtp1");
export const automationVerifyOtpUrl = () => automationApiPostUrl("verifyOtp");
export const automationVerifyPanUrl = () => automationApiPostUrl("verifyPan");
export const automationUpdateEmailUrl = () =>
  automationApiPostUrl("updateEmail");
export const automationSendAadhaarOtpUrl = () =>
  automationApiPostUrl("sendAadhaarOtp");
export const automationSaveEmploymentUrl = () =>
  automationApiPostUrl("saveEmployment");
export const automationSaveCustomerReferencesUrl = () =>
  automationApiPostUrl("saveCustomerReferences");
export const automationUpdateCustomerReferencesUrl = () =>
  automationApiPostUrl("updateCustomerReferences");

export const eMandateUrl = () => `${API_ORIGIN}/payu/mandate_form`;

/** Cross-vertical PAN bucket: GET /Pansearch/checkPan/:pan */
export const panSearchUrl = (pan) => {
  const path = `/Pansearch/checkPan/${encodeURIComponent(String(pan || "").trim().toUpperCase())}`;
  return process.env.NODE_ENV === "development" ? path : `${API_ORIGIN}${path}`;
};

const GENERIC_WRAPPER_PATTERNS = [
  "please correct the following errors",
  "validation failed",
  "the given data was invalid",
];

const isGenericWrapperMessage = (text) => {
  const lower = String(text || "").toLowerCase().trim();
  return GENERIC_WRAPPER_PATTERNS.some((pattern) => lower.includes(pattern));
};

const looksLikeHtmlOrScriptPage = (value) => {
  const text = String(value || "").toLowerCase();
  if (!text) return false;
  return (
    text.includes("<!doctype") ||
    text.includes("<html") ||
    text.includes("<head") ||
    text.includes("<body") ||
    text.includes("fbq(") ||
    text.includes("fbevents.js") ||
    text.includes("/static/js/") ||
    text.includes("/static/css/")
  );
};

const cleanApiText = (value) => {
  if (value == null || value === false) return "";
  if (typeof value === "string") {
    if (looksLikeHtmlOrScriptPage(value)) return "";
    const cleaned = value.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").trim();
    if (looksLikeHtmlOrScriptPage(cleaned) || cleaned.length > 280) return "";
    return cleaned;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return "";
};

/** Flatten API `errors` arrays/objects into user-facing text. */
export const extractApiErrors = (value) => {
  if (value == null) return "";

  if (Array.isArray(value)) {
    return value
      .map((item) => extractApiErrors(item))
      .filter(Boolean)
      .join("\n");
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return cleanApiText(value);
  }

  if (typeof value === "object") {
    const direct = cleanApiText(
      value.message || value.Message || value.error || value.msg || value.Msg
    );
    if (direct) return direct;

    const nested = Object.values(value)
      .map((item) => extractApiErrors(item))
      .filter(Boolean)
      .join("\n");
    return nested;
  }

  return "";
};

/**
 * Prefer specific API errors over generic wrapper messages
 * e.g. errors: ["Please select a valid city."] beats message: "Please correct the following errors."
 */
export const getApiMessage = (data, fallback = "") => {
  if (data == null) return fallback;

  if (typeof data === "string") {
    if (looksLikeHtmlOrScriptPage(data)) return fallback;
    const cleaned = cleanApiText(data);
    if (cleaned && !looksLikeHtmlOrScriptPage(cleaned)) {
      return cleaned;
    }
    return fallback;
  }

  if (Array.isArray(data)) {
    const joined = data
      .map((item) => getApiMessage(item, ""))
      .filter(Boolean)
      .join("\n");
    return joined || fallback;
  }

  if (typeof data !== "object") {
    const text = String(data).trim();
    return text || fallback;
  }

  const errorsText = extractApiErrors(
    data.errors ?? data.Errors ?? data.data?.errors ?? data.data?.Errors
  );
  const mainMessage = cleanApiText(
    data.message ??
      data.Message ??
      data.msg ??
      data.Msg ??
      data.error ??
      data.Error ??
      data.responseMessage ??
      data.response_message ??
      data.api_message ??
      data.api_response?.message ??
      data.api_response?.Message ??
      data.data?.message ??
      data.data?.Message
  );

  if (errorsText) {
    if (!mainMessage || isGenericWrapperMessage(mainMessage) || errorsText !== mainMessage) {
      return errorsText;
    }
  }

  if (mainMessage) return mainMessage;

  const nested = getApiMessage(data.data, "");
  if (nested) return nested;

  return fallback;
};

export const formatApiMessage = (message) => {
  const text = getApiMessage(message, "");
  return text || "Something went wrong. Please try again.";
};

/** Normalize success from TaskApi or loan_backend save responses */
export const isLeadSaveSuccess = (data) => {
  if (data == null) return false;
  if (typeof data === "string") {
    const t = data.toLowerCase();
    return t.includes("success") && !t.includes("unsuccess");
  }
  if (typeof data !== "object") return false;

  const status = data.Status ?? data.status;
  if (status === 1 || status === "1" || status === true) return true;
  if (data.success === true || data.Success === true) return true;

  const msg = String(data.Message || data.message || data.msg || "").toLowerCase();
  if (
    msg.includes("success") &&
    !msg.includes("unsuccess") &&
    status !== 0 &&
    status !== "0" &&
    data.success !== false
  ) {
    return true;
  }
  return false;
};

export const getLeadSaveMessage = (
  data,
  fallback = "Something went wrong. Please try again."
) => getApiMessage(data, fallback);

export const getLeadInsertedId = (data) =>
  data?.inserted_id ||
  data?.insertedId ||
  data?.lead_id ||
  data?.leadId ||
  data?.Data?.inserted_id ||
  data?.Data?.lead_id ||
  data?.data?.inserted_id ||
  data?.data?.lead_id ||
  data?.id ||
  null;
